import { useEffect, useState, useMemo, useCallback } from 'react';
import { FiMonitor, FiPlus, FiEdit2, FiTrash2, FiCheckCircle, FiXCircle, FiFilter, FiSearch, FiX, FiSave } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';
import { useScopedSiteFilter } from '../hooks/useScopedSiteFilter';
import { CRUD_UI } from '../components/crudUi';

const Stations = () => {
  const [stations, setStations] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [filterSite, setFilterSite] = useState('');
  const [filterModality, setFilterModality] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { applySiteScope } = useScopedSiteFilter();
  const [formData, setFormData] = useState({
    site_id: '',
    name: '',
    ae_title: '',
    station_name: '',
    modality: '',
    ip_address: '',
    port: '',
    description: '',
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  useEffect(() => {
    fetchStations();
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await api.get('/orthanc/sites');
      setSites(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchStations = async () => {
    try {
      let params = {};
      if (filterSite) params.site_id = filterSite;
      if (filterModality) params.modality = filterModality;
      params = applySiteScope(params, filterSite);
      
      const response = await api.get('/stations', { params });
      let stationsData = response.data.data || [];
      
      // Apply search filter on client side
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        stationsData = stationsData.filter(station => 
          station.name?.toLowerCase().includes(searchLower) ||
          station.ae_title?.toLowerCase().includes(searchLower) ||
          station.ip_address?.toLowerCase().includes(searchLower) ||
          station.station_name?.toLowerCase().includes(searchLower) ||
          station.site_name?.toLowerCase().includes(searchLower)
        );
      }
      
      setStations(stationsData);
    } catch (error) {
      toast.error('Không thể tải danh sách máy XQ');
    } finally {
      setLoading(false);
    }
  };

  // Pagination calculations
  const totalItems = stations.length;
  const paginatedStations = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return stations.slice(startIndex, endIndex);
  }, [stations, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterSite, filterModality, searchTerm]);

  // Handle page change - use useCallback to ensure stable reference
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Handle items per page change - use useCallback to ensure stable reference
  const handleItemsPerPageChange = useCallback((value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    fetchStations();
  }, [filterSite, filterModality, searchTerm]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const errors = validateStationForm();
      setFormErrors(errors);
      setTouchedFields({ site_id: true, modality: true, name: true, ae_title: true, port: true, ip_address: true });
      if (Object.keys(errors).length > 0) {
        toast.error('Vui lòng kiểm tra lại dữ liệu');
        return;
      }

      const payload = {
        ...formData,
        port: formData.port ? parseInt(formData.port) : null,
      };
      await api.post('/stations', payload);
      toast.success('Tạo máy XQ thành công');
      setShowModal(false);
      resetForm();
      fetchStations();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Không thể tạo máy XQ');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const errors = validateStationForm();
      setFormErrors(errors);
      setTouchedFields({ site_id: true, modality: true, name: true, ae_title: true, port: true, ip_address: true });
      if (Object.keys(errors).length > 0) {
        toast.error('Vui lòng kiểm tra lại dữ liệu');
        return;
      }

      const payload = {
        ...formData,
        port: formData.port ? parseInt(formData.port) : null,
      };
      await api.put(`/stations/${editingStation.id}`, payload);
      toast.success('Cập nhật máy XQ thành công');
      setShowModal(false);
      setEditingStation(null);
      resetForm();
      fetchStations();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Không thể cập nhật máy XQ');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa máy XQ này?')) {
      return;
    }

    try {
      await api.delete(`/stations/${id}`);
      toast.success('Xóa máy XQ thành công');
      fetchStations();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Không thể xóa máy XQ');
    }
  };

  const handleEdit = (station) => {
    setEditingStation(station);
    setFormData({
      site_id: station.site_id,
      name: station.name,
      ae_title: station.ae_title,
      station_name: station.station_name || station.name,
      modality: station.modality,
      ip_address: station.ip_address || '',
      port: station.port || '',
      description: station.description || '',
      is_active: station.is_active !== undefined ? station.is_active : true,
    });
    setFormErrors({});
    setTouchedFields({});
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      site_id: '',
      name: '',
      ae_title: '',
      station_name: '',
      modality: '',
      ip_address: '',
      port: '',
      description: '',
      is_active: true,
    });
    setFormErrors({});
    setTouchedFields({});
    setEditingStation(null);
  };

  const isValidIpv4 = (value) => {
    const parts = value.split('.');
    if (parts.length !== 4) return false;
    return parts.every((part) => /^\d+$/.test(part) && Number(part) >= 0 && Number(part) <= 255);
  };

  const validateStationField = (field, value) => {
    if (field === 'site_id' && !value) return 'Vui lòng chọn cơ sở';
    if (field === 'modality' && !value) return 'Vui lòng chọn loại máy';
    if (field === 'name') {
      if (!value?.trim()) return 'Tên máy là bắt buộc';
      if (value.trim().length > 100) return 'Tên máy tối đa 100 ký tự';
    }
    if (field === 'ae_title') {
      const normalized = (value || '').trim();
      if (!normalized) return 'AE Title là bắt buộc';
      if (normalized.length > 16) return 'AE Title tối đa 16 ký tự';
      if (!/^[A-Z0-9_]+$/.test(normalized)) return 'AE Title chỉ gồm A-Z, 0-9, _';
    }
    if (field === 'ip_address' && value?.trim()) {
      if (!isValidIpv4(value.trim())) return 'IP Address không hợp lệ (vd: 192.168.1.100)';
    }
    if (field === 'port' && value !== '' && value !== null && value !== undefined) {
      const port = Number(value);
      if (!Number.isInteger(port) || port < 1 || port > 65535) return 'Port phải trong khoảng 1-65535';
    }
    return '';
  };

  const validateStationForm = () => {
    const errors = {};
    ['site_id', 'modality', 'name', 'ae_title', 'ip_address', 'port'].forEach((field) => {
      const error = validateStationField(field, formData[field]);
      if (error) errors[field] = error;
    });
    return errors;
  };

  const updateStationField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };
      const error = validateStationField(field, value);
      if (error) nextErrors[field] = error;
      else delete nextErrors[field];
      return nextErrors;
    });
  };

  const getModalityBadge = (modality) => {
    const colors = {
      CR: 'bg-blue-100 text-blue-800',
      CT: 'bg-cyan-100 text-cyan-800',
      MR: 'bg-green-100 text-green-800',
      US: 'bg-yellow-100 text-yellow-800',
      DX: 'bg-gray-100 text-gray-800',
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[modality] || 'bg-blue-100 text-blue-800'}`}>{modality}</span>;
  };

  if (loading) {
    return (
      <div className={CRUD_UI.loadingWrap}>
        <div className={CRUD_UI.loadingSpinner}></div>
        <p className="text-slate-500 text-sm">Đang tải danh sách máy XQ...</p>
      </div>
    );
  }

  return (
    <div className={CRUD_UI.page}>
      {/* Search and Filters */}
      <div className={CRUD_UI.filterCard}>
        <div className={`${CRUD_UI.filterHeader} justify-between flex-wrap gap-2`}>
          <div className="flex items-center gap-2">
            <FiFilter className="text-slate-500 text-xl" />
            <span className="text-sm font-semibold text-slate-900">Tìm kiếm và bộ lọc</span>
          </div>
          <button
            className={CRUD_UI.primaryButton}
            onClick={() => { resetForm(); setShowModal(true); }}
          >
            <FiPlus className="text-base" />
            Thêm máy XQ
          </button>
        </div>
        <div className="flex gap-4 flex-wrap">
          <div className={CRUD_UI.filterFieldWide}>
            <label className={CRUD_UI.filterLabel}>Tìm kiếm</label>
            <div className={CRUD_UI.searchWrap}>
              <FiSearch className={CRUD_UI.searchIcon} />
              <input
                type="text"
                className={CRUD_UI.searchInput}
                placeholder="Tìm theo tên, AE Title, IP, cơ sở..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className={CRUD_UI.searchClearButton}
                  onClick={() => setSearchTerm('')}
                  title="Xóa tìm kiếm"
                >
                  <FiX className="text-base" />
                </button>
              )}
            </div>
          </div>
          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Cơ sở</label>
            <select
              className={CRUD_UI.filterSelect}
              value={filterSite}
              onChange={(e) => setFilterSite(e.target.value)}
            >
              <option value="">Tất cả cơ sở</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Loại máy</label>
            <select
              className={CRUD_UI.filterSelect}
              value={filterModality}
              onChange={(e) => setFilterModality(e.target.value)}
            >
              <option value="">Tất cả loại máy</option>
              <option value="CR">CR - Computed Radiography</option>
              <option value="CT">CT - Computed Tomography</option>
              <option value="MR">MR - Magnetic Resonance</option>
              <option value="US">US - Ultrasound</option>
              <option value="DX">DX - Digital Radiography</option>
            </select>
          </div>
        </div>
      </div>

      <div className={CRUD_UI.tableViewportShell}>
        <div className={CRUD_UI.tableViewportScroll}>
          <table className={CRUD_UI.table} key={`stations-table-${currentPage}-${itemsPerPage}`}>
            <thead className={CRUD_UI.tableHead}>
              <tr>
                <th className={CRUD_UI.tableHeaderCell}>Tên máy</th>
                <th className={CRUD_UI.tableHeaderCell}>AE Title</th>
                <th className={CRUD_UI.tableHeaderCell}>Cơ sở</th>
                <th className={CRUD_UI.tableHeaderCell}>Loại máy</th>
                <th className={CRUD_UI.tableHeaderCell}>IP Address</th>
                <th className={CRUD_UI.tableHeaderCell}>Port</th>
                <th className={CRUD_UI.tableHeaderCell}>Trạng thái</th>
                <th className={CRUD_UI.tableHeaderCell}>Thao tác</th>
              </tr>
            </thead>
            <tbody key={`page-${currentPage}-items-${itemsPerPage}`}>
              {paginatedStations.length === 0 ? (
                <tr>
                  <td colSpan="8" className={CRUD_UI.emptyCell}>
                    <div className="flex flex-col items-center gap-2">
                      <FiMonitor className="text-5xl text-slate-300" />
                      <p className="text-slate-500 text-sm">Chưa có máy XQ nào</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedStations.map((station) => (
                  <tr key={station.id} className={CRUD_UI.tableRow}>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex flex-col">
                        <strong className="text-slate-900">{station.name}</strong>
                        {station.station_name && station.station_name !== station.name && (
                          <span className="text-xs text-slate-500 mt-0.5">{station.station_name}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{station.ae_title}</code>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">{station.site_name || 'N/A'}</td>
                    <td className="px-3 py-2 text-sm">{getModalityBadge(station.modality)}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{station.ip_address || '-'}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{station.port || '-'}</td>
                    <td className="px-3 py-2 text-sm">
                      {station.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700">
                          <FiCheckCircle className="text-xs" />
                          Hoạt động
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide bg-red-100 text-red-700">
                          <FiXCircle className="text-xs" />
                          Tạm ngưng
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center gap-1">
                        <button
                          className={CRUD_UI.iconButtonEdit}
                          onClick={() => handleEdit(station)}
                          title="Chỉnh sửa"
                        >
                          <FiEdit2 className="text-base" />
                        </button>
                        <button
                          className={CRUD_UI.iconButtonDelete}
                          onClick={() => handleDelete(station.id)}
                          title="Xóa"
                        >
                          <FiTrash2 className="text-base" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalItems > 0 && (
        <div className={CRUD_UI.paginationDock}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        </div>
      )}

      {showModal && (
        <div className={CRUD_UI.modalOverlay}>
          <div className={CRUD_UI.modalContainer} onClick={(e) => e.stopPropagation()}>
            <div className={CRUD_UI.modalHeader}>
              <h2 className={CRUD_UI.modalTitle}>{editingStation ? 'Chỉnh sửa máy XQ' : 'Thêm máy XQ mới'}</h2>
              <button 
                className={CRUD_UI.modalCloseButton}
                onClick={() => { setShowModal(false); resetForm(); }}
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={editingStation ? handleUpdate : handleCreate} className={CRUD_UI.modalForm}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Cơ sở <span className="text-red-500">*</span></label>
                  <select
                    className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.site_id && formErrors.site_id ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.site_id}
                    onChange={(e) => updateStationField('site_id', e.target.value)}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, site_id: true }))}
                    required
                  >
                    <option value="">Chọn cơ sở</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                  {touchedFields.site_id && formErrors.site_id && <p className="mt-1 text-xs text-red-600">{formErrors.site_id}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Loại máy <span className="text-red-500">*</span></label>
                  <select
                    className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.modality && formErrors.modality ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.modality}
                    onChange={(e) => updateStationField('modality', e.target.value)}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, modality: true }))}
                    required
                  >
                    <option value="">Chọn loại máy</option>
                    <option value="CR">CR - Computed Radiography</option>
                    <option value="CT">CT - Computed Tomography</option>
                    <option value="MR">MR - Magnetic Resonance</option>
                    <option value="US">US - Ultrasound</option>
                    <option value="DX">DX - Digital Radiography</option>
                  </select>
                  {touchedFields.modality && formErrors.modality && <p className="mt-1 text-xs text-red-600">{formErrors.modality}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Tên máy <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.name && formErrors.name ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.name}
                    onChange={(e) => updateStationField('name', e.target.value)}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, name: true }))}
                    placeholder="Máy XQ Phòng 1"
                    required
                  />
                  {touchedFields.name && formErrors.name && <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Station Name</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                    value={formData.station_name}
                    onChange={(e) => setFormData({ ...formData, station_name: e.target.value })}
                    placeholder="X-Ray Room 1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">AE Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.ae_title && formErrors.ae_title ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.ae_title}
                    onChange={(e) => updateStationField('ae_title', e.target.value.toUpperCase())}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, ae_title: true }))}
                    placeholder="XQ_ROOM1"
                    maxLength={16}
                    required
                  />
                  {touchedFields.ae_title && formErrors.ae_title && <p className="mt-1 text-xs text-red-600">{formErrors.ae_title}</p>}
                  <small className="text-xs text-slate-500 mt-1 block">DICOM AE Title (tối đa 16 ký tự)</small>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Trạng thái</label>
                  <select
                    className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                    value={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                  >
                    <option value={true}>Hoạt động</option>
                    <option value={false}>Tạm ngưng</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">IP Address</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.ip_address && formErrors.ip_address ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.ip_address}
                    onChange={(e) => updateStationField('ip_address', e.target.value)}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, ip_address: true }))}
                    placeholder="192.168.1.100"
                  />
                  {touchedFields.ip_address && formErrors.ip_address && <p className="mt-1 text-xs text-red-600">{formErrors.ip_address}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Port</label>
                  <input
                    type="number"
                    className={`w-full px-3 py-2 border rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary ${touchedFields.port && formErrors.port ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.port}
                    onChange={(e) => updateStationField('port', e.target.value)}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, port: true }))}
                    placeholder="104"
                  />
                  {touchedFields.port && formErrors.port && <p className="mt-1 text-xs text-red-600">{formErrors.port}</p>}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs font-medium text-slate-700 mb-1.5">Mô tả</label>
                <textarea
                  className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary resize-none"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Mô tả về máy XQ này..."
                />
              </div>

              <div className={CRUD_UI.modalFooter}>
                <button 
                  type="button" 
                  className={CRUD_UI.secondaryButton}
                  onClick={() => { setShowModal(false); resetForm(); }}
                >
                  <FiX className="text-base" />
                  Hủy
                </button>
                <button 
                  type="submit" 
                  className={CRUD_UI.primaryButton}
                >
                  <FiSave className="text-base" />
                  {editingStation ? 'Cập nhật' : 'Tạo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stations;


